import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import * as QRCode from 'qrcode';
import { PopupBaseComponent } from '../../../../../shared/components/popup/popup-base/popup-base.component';
import { ButtonComponent, ButtonType } from '../../../../../shared/components/button/button.component';
import { SpinnerComponent } from '../../../../../shared/components/spinner/spinner.component';
import { AuthenticationService } from '../../../../../core/authentication/authentication.service';
import { LayoutStore, LayoutStoreType } from '../../../../../core/store/layout.store';
import { TwoFactorSetupPopupModel } from './two-factor-setup-popup.model';

type SetupStep = 'qr' | 'verify';

@Component({
    selector: 'juyo-two-factor-setup-popup',
    styleUrl: './two-factor-setup-popup.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopupBaseComponent, ButtonComponent, SpinnerComponent, ReactiveFormsModule],
    template: `
        <juyo-popup-base [popupId]="model().id" [showHeader]="true" [maxWidth]="'520px'">
            <span slot="header">Set Up Two-Factor Authentication</span>

            <div slot="body" class="body">
                @if (setupError()) {
                    <p class="error-text">
                        Something went wrong while preparing the setup. Please close this dialog and try again.
                    </p>
                } @else if (step() === 'qr') {
                    <p class="description">
                        Scan the QR code below with your authenticator app (e.g. Google Authenticator, Authy).
                    </p>
                    <div class="qr-wrapper">
                        @if (isGenerating()) {
                            <juyo-spinner></juyo-spinner>
                        } @else {
                            <img class="qr-image" [src]="qrDataUrl()" alt="Two-factor authentication QR code" />
                        }
                    </div>
                    <div class="field-group">
                        <label class="field-label" for="manualKey">Or enter the key manually</label>
                        <input id="manualKey" class="field" type="text" [value]="sharedSecret()" readonly />
                    </div>
                } @else {
                    <p class="description">
                        Enter the 6-digit code from your authenticator app to finish setting up.
                    </p>
                    <div class="field-group">
                        <label class="field-label" for="totpCode">Verification code</label>
                        <input
                            id="totpCode"
                            class="field"
                            type="text"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            maxlength="6"
                            placeholder="123456"
                            [class.invalid]="!!errorMessage()"
                            [formControl]="code"
                        />
                        @if (errorMessage()) {
                            <span class="error-text">{{ errorMessage() }}</span>
                        }
                    </div>
                }
            </div>

            <ng-container slot="footer">
                @if (step() === 'qr') {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="onCancel()">Cancel</juyo-button>
                    <juyo-button
                        [buttonType]="ButtonType.PURPLE"
                        [disabled]="isGenerating() || setupError()"
                        (clicked)="onNext()"
                    >
                        Next
                    </juyo-button>
                } @else {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="onBack()">Back</juyo-button>
                    <juyo-button
                        [buttonType]="ButtonType.PURPLE"
                        [disabled]="!canVerify()"
                        [isLoading]="isVerifying()"
                        (clicked)="onVerify()"
                    >
                        Verify
                    </juyo-button>
                }
            </ng-container>
        </juyo-popup-base>
    `,
})
export class TwoFactorSetupPopupComponent implements OnInit {
    public model = input.required<TwoFactorSetupPopupModel>();

    protected readonly ButtonType = ButtonType;

    protected step = signal<SetupStep>('qr');
    protected sharedSecret = signal('');
    protected qrDataUrl = signal('');
    protected isGenerating = signal(true);
    protected isVerifying = signal(false);
    protected setupError = signal(false);
    protected errorMessage = signal('');

    protected code = new FormControl<string>('', { nonNullable: true });
    private codeValue = toSignal(this.code.valueChanges, { initialValue: '' });
    protected canVerify = computed(() => this.codeValue().trim().length === 6 && !this.isVerifying());

    private authService = inject(AuthenticationService);
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private destroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        this.authService
            .setupTOTP()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (output) => {
                    this.sharedSecret.set(output.sharedSecret);
                    const uri = output.getSetupUri('Juyo', this.model().accountName).toString();
                    QRCode.toDataURL(uri, { margin: 1, width: 240 })
                        .then((url) => {
                            this.qrDataUrl.set(url);
                            this.isGenerating.set(false);
                        })
                        .catch(() => {
                            this.isGenerating.set(false);
                            this.setupError.set(true);
                        });
                },
                error: () => {
                    this.isGenerating.set(false);
                    this.setupError.set(true);
                },
            });
    }

    protected onNext(): void {
        this.step.set('verify');
    }

    protected onBack(): void {
        this.errorMessage.set('');
        this.step.set('qr');
    }

    protected onVerify(): void {
        const code = this.code.value.trim();
        if (code.length !== 6 || this.isVerifying()) {
            return;
        }

        this.isVerifying.set(true);
        this.errorMessage.set('');
        this.authService
            .verifyAndEnableTOTP(code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.isVerifying.set(false);
                    this.model().onSuccess();
                    this.layoutStore.closePopup(this.model().id);
                },
                error: () => {
                    this.isVerifying.set(false);
                    this.errorMessage.set('That code did not work. Please try again.');
                },
            });
    }

    protected onCancel(): void {
        this.layoutStore.closePopup(this.model().id);
    }
}
